import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type FormFieldType = 'text' | 'longtext' | 'email' | 'select' | 'date' | 'number';

export interface FormField {
  id: string;
  type: FormFieldType;
  label: string;
  required: boolean;
  placeholder?: string;
  options?: string[];      // for select
  columnId?: string;       // which board column to store into (optional)
}

export interface Form {
  id: string;
  slug: string;            // public URL token
  boardId: string;         // items submitted go into this board
  groupId?: string;        // default group id
  name: string;
  description?: string;
  fields: FormField[];
  accent: string;          // gradient tail
  icon: string;
  submissionCount: number;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
}

const uid = (p = 'id') => `${p}_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36).slice(-4)}`;
const slug = () => Math.random().toString(36).slice(2, 10);
const now = () => new Date().toISOString();

function makeSeed(): Form[] {
  return [{
    id: 'form_feedback',
    slug: 'feedback-q2',
    boardId: 'bd_marketing',
    name: 'Customer feedback',
    description: 'Tell us what\'s working and what\'s not. Every response becomes an item on our Marketing Launch Q2 board.',
    accent: 'from-neon-pink to-neon-purple',
    icon: '',
    isPublic: true,
    submissionCount: 0,
    createdAt: now(),
    updatedAt: now(),
    fields: [
      { id: uid('fld'), type: 'text',     label: 'Your name',      required: true,  placeholder: 'e.g. Alex' },
      { id: uid('fld'), type: 'email',    label: 'Email',          required: true,  placeholder: 'you@company.com' },
      { id: uid('fld'), type: 'select',   label: 'How are we doing?', required: true, options: ['Amazing', 'Good', 'Meh', 'Needs work'] },
      { id: uid('fld'), type: 'longtext', label: 'Anything else?', required: false, placeholder: 'Tell us more…' },
    ],
  }];
}

interface FormsState {
  forms: Form[];
  createForm: (boardId: string, name: string) => string;
  renameForm: (id: string, name: string) => void;
  updateForm: (id: string, patch: Partial<Form>) => void;
  deleteForm: (id: string) => void;
  addField: (formId: string, field: Omit<FormField, 'id'>) => string;
  updateField: (formId: string, fieldId: string, patch: Partial<FormField>) => void;
  removeField: (formId: string, fieldId: string) => void;
  incrementSubmissionCount: (id: string) => void;
  resetToSeed: () => void;
}

export const useFormsStore = create<FormsState>()(
  persist(
    (set) => ({
      forms: makeSeed(),
      createForm: (boardId, name) => {
        const id = uid('form');
        set((s) => ({
          forms: [...s.forms, {
            id,
            slug: slug(),
            boardId,
            name,
            description: '',
            accent: 'from-neon-purple to-neon-blue',
            icon: '',
            isPublic: true,
            submissionCount: 0,
            fields: [
              { id: uid('fld'), type: 'text',  label: 'Name',  required: true },
              { id: uid('fld'), type: 'email', label: 'Email', required: true },
            ],
            createdAt: now(),
            updatedAt: now(),
          }],
        }));
        return id;
      },
      renameForm: (id, name) => set((s) => ({
        forms: s.forms.map((f) => f.id === id ? { ...f, name, updatedAt: now() } : f),
      })),
      updateForm: (id, patch) => set((s) => ({
        forms: s.forms.map((f) => f.id === id ? { ...f, ...patch, updatedAt: now() } : f),
      })),
      deleteForm: (id) => set((s) => ({
        forms: s.forms.filter((f) => f.id !== id),
      })),
      addField: (formId, field) => {
        const id = uid('fld');
        set((s) => ({
          forms: s.forms.map((f) => f.id === formId
            ? { ...f, fields: [...f.fields, { ...field, id }], updatedAt: now() }
            : f,
          ),
        }));
        return id;
      },
      updateField: (formId, fieldId, patch) => set((s) => ({
        forms: s.forms.map((f) => f.id === formId
          ? { ...f, fields: f.fields.map((fld) => fld.id === fieldId ? { ...fld, ...patch } : fld), updatedAt: now() }
          : f,
        ),
      })),
      removeField: (formId, fieldId) => set((s) => ({
        forms: s.forms.map((f) => f.id === formId
          ? { ...f, fields: f.fields.filter((fld) => fld.id !== fieldId), updatedAt: now() }
          : f,
        ),
      })),
      incrementSubmissionCount: (id) => set((s) => ({
        forms: s.forms.map((f) => f.id === id ? { ...f, submissionCount: f.submissionCount + 1 } : f),
      })),
      resetToSeed: () => set({ forms: makeSeed() }),
    }),
    { name: 'myday-forms', version: 1 },
  ),
);
